import React, { useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import apiClient from "services/api/apiClient";
import { ENDPOINTS, API_BASE_URL } from "services/api/endpoints";
import { getAccessToken } from "utils/auth";

// Stammdaten-Arbeitsmappe: Materialien, Sektoren, Listen und ihre Zuordnungen
// als Excel herunterladen, bearbeiten und wieder hochladen.
// Backend: app/api/routes/admin.py (_build_material_workbook / _import_material_workbook).
// Ablauf: Datei wählen -> Vorschau (dry_run=true, Backend rechnet alles durch und
// rollt zurück) -> Übernehmen (dieselbe Datei ohne dry_run) -> Ergebnis.

const btn = (primary = false, disabled = false, danger = false) => ({
  padding: "10px 14px",
  borderRadius: 12,
  border: primary ? "none" : "1px solid #cbd5e1",
  background: disabled ? "#e2e8f0" : primary ? (danger ? "#b91c1c" : "#1f3864") : "#fff",
  color: disabled ? "#94a3b8" : primary ? "#fff" : "#1e293b",
  fontWeight: 600,
  cursor: disabled ? "not-allowed" : "pointer",
});

const card = {
  background: "var(--au-surface, #fff)",
  // Eigene Textfarbe: die dunkle Admin-Seite setzt helle Schrift, auf der hellen Karte unlesbar.
  color: "var(--au-text, #1e293b)",
  border: "1px solid var(--au-border, #e2e8f0)",
  borderRadius: 14,
  padding: 16,
};
const cardTitle = {
  fontSize: 11,
  textTransform: "uppercase",
  letterSpacing: "0.07em",
  color: "var(--au-faint, #64748b)",
  fontWeight: 600,
  marginBottom: 12,
};
const previewBox = {
  marginTop: 16,
  padding: 14,
  borderRadius: 12,
  border: "1px solid #fcd34d",
  background: "#fffbeb",
  color: "#1e293b",
};
const changeList = {
  margin: "8px 0 0",
  padding: "8px 12px 8px 28px",
  maxHeight: 280,
  overflowY: "auto",
  background: "#fff",
  border: "1px solid #e2e8f0",
  borderRadius: 8,
  fontSize: 13,
};
const th = { textAlign: "left", padding: "6px 8px", borderBottom: "1px solid #e2e8f0", fontSize: 13 };
const td = { padding: "6px 8px", borderBottom: "1px solid #f1f5f9", fontSize: 13 };

// Höchstzahl der Änderungszeilen, die das Backend mitschickt.
const MAX_CHANGE_LINES = 300;

const ENTITIES = ["materials", "sectors", "lists"];

const formatSize = (bytes) => {
  if (!Number.isFinite(bytes)) return "";
  if (bytes < 1024 * 1024) return `${Math.max(1, Math.round(bytes / 1024))} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
};

const errorText = (err, fallback) => {
  const detail = err?.data?.detail;
  if (typeof detail === "string" && detail) return detail;
  if (Array.isArray(detail) && detail.length) return detail.map((d) => d?.msg || String(d)).join("; ");
  return err?.message || fallback;
};

const num = (v) => (Number.isFinite(Number(v)) ? Number(v) : 0);

// Zeilen, die etwas wegnehmen: Deaktivierungen und entfernte Zuordnungen (nicht "0 entfernt").
export const isRemovalLine = (line) => /deaktiviert\s*$/i.test(line) || /(^|\D)[1-9]\d*\s+entfernt\s*$/i.test(line);

export const hasChanges = (result) => {
  if (!result) return false;
  const counts =
    ENTITIES.reduce(
      (n, e) => n + num(result[`${e}_created`]) + num(result[`${e}_updated`]) + num(result[`${e}_deleted`]),
      0
    ) +
    num(result.material_fields_changed) +
    num(result.memberships_added) +
    num(result.memberships_removed);
  return counts > 0 || (Array.isArray(result.changes) && result.changes.length > 0);
};

function ResultSummary({ t, result }) {
  const deleted = ENTITIES.reduce((n, e) => n + num(result[`${e}_deleted`]), 0);
  const changes = Array.isArray(result.changes) ? result.changes : [];
  return (
    <>
      <table style={{ borderCollapse: "collapse", marginTop: 8, minWidth: 360 }}>
        <thead>
          <tr>
            <th style={th}></th>
            <th style={th}>{t("admin.masterData.result.created")}</th>
            <th style={th}>{t("admin.masterData.result.updated")}</th>
            <th style={th}>{t("admin.masterData.result.deleted")}</th>
          </tr>
        </thead>
        <tbody>
          {ENTITIES.map((e) => (
            <tr key={e}>
              <td style={{ ...td, fontWeight: 600 }}>{t(`admin.masterData.result.${e}`)}</td>
              <td style={td}>{num(result[`${e}_created`])}</td>
              <td style={td}>{num(result[`${e}_updated`])}</td>
              <td style={{ ...td, color: num(result[`${e}_deleted`]) ? "#b91c1c" : undefined }}>
                {num(result[`${e}_deleted`])}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
      <p style={{ margin: "8px 0 0" }}>
        {t("admin.masterData.result.fieldsChanged", { count: num(result.material_fields_changed) })}
      </p>
      <p style={{ margin: "4px 0 0" }}>
        {t("admin.masterData.result.membershipsAdded", { count: num(result.memberships_added) })}
        {" · "}
        <span style={{ color: num(result.memberships_removed) ? "#b91c1c" : undefined }}>
          {t("admin.masterData.result.membershipsRemoved", { count: num(result.memberships_removed) })}
        </span>
      </p>
      {deleted > 0 && (
        <p style={{ color: "#b91c1c", margin: "6px 0 0" }}>
          {t("admin.masterData.result.deletedWarning", { count: deleted })}
        </p>
      )}
      {changes.length > 0 && (
        <>
          <ul style={changeList} aria-label={t("admin.masterData.changes.title")}>
            {changes.map((line, i) => (
              <li key={i} style={isRemovalLine(line) ? { color: "#b91c1c", fontWeight: 600 } : undefined}>
                {line}
              </li>
            ))}
          </ul>
          {changes.length >= MAX_CHANGE_LINES && (
            <p style={{ margin: "4px 0 0", fontSize: 13, color: "var(--au-faint, #64748b)" }}>
              {t("admin.masterData.changes.truncated", { count: MAX_CHANGE_LINES })}
            </p>
          )}
        </>
      )}
    </>
  );
}

export default function MasterDataSection() {
  const { t } = useTranslation();
  const fileInputRef = useRef(null);
  const [file, setFile] = useState(null);
  const [busy, setBusy] = useState("");
  const [error, setError] = useState("");
  const [preview, setPreview] = useState(null);
  const [applied, setApplied] = useState(null);

  // Datei-Download braucht die Rohantwort (Blob); der apiClient liest immer JSON.
  // Abgelaufene Tokens erneuert der globale fetch-Interceptor.
  const download = async () => {
    setBusy("download");
    setError("");
    try {
      const token = getAccessToken();
      const res = await fetch(`${API_BASE_URL}${ENDPOINTS.ADMIN.MATERIAL_WORKBOOK_EXPORT}`, {
        credentials: "include",
        headers: token ? { Authorization: `Bearer ${token}` } : {},
      });
      if (!res.ok) throw new Error(t("admin.masterData.downloadFailed"));
      const blob = await res.blob();
      const cd = res.headers.get("Content-Disposition") || "";
      const name = (cd.match(/filename="([^"]+)"/) || [])[1] || "stammdaten.xlsx";
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = name;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
    } catch (e) {
      setError(e?.message || t("admin.masterData.downloadFailed"));
    } finally {
      setBusy("");
    }
  };

  const pickFile = (e) => {
    const f = e.target.files?.[0] || null;
    setPreview(null);
    setApplied(null);
    setError("");
    if (f && !f.name.toLowerCase().endsWith(".xlsx")) {
      setFile(null);
      setError(t("admin.masterData.onlyXlsx"));
      return;
    }
    setFile(f);
  };

  // Kein automatischer Wiederholversuch und kein 60-s-Timeout des Clients
  // (eigener Signal-Controller ohne Zeitlimit): große Dateien brauchen länger.
  const send = async (dryRun) => {
    const body = new FormData();
    body.append("file", file);
    const endpoint = `${ENDPOINTS.ADMIN.MATERIAL_WORKBOOK_IMPORT}${dryRun ? "?dry_run=true" : ""}`;
    return apiClient.post(endpoint, body, { retry: false, signal: new AbortController().signal });
  };

  const runPreview = async () => {
    if (!file) return;
    setBusy("preview");
    setError("");
    setApplied(null);
    try {
      const data = await send(true);
      setPreview(data || {});
    } catch (e) {
      setPreview(null);
      setError(errorText(e, t("admin.masterData.previewFailed")));
    } finally {
      setBusy("");
    }
  };

  const apply = async () => {
    if (!file || !hasChanges(preview)) return;
    setBusy("apply");
    setError("");
    try {
      const data = await send(false);
      setApplied(data || {});
      setPreview(null);
      setFile(null);
      if (fileInputRef.current) fileInputRef.current.value = "";
    } catch (e) {
      setError(`${errorText(e, t("admin.masterData.applyFailed"))} ${t("admin.masterData.applyFailedHint")}`);
    } finally {
      setBusy("");
    }
  };

  const previewHasChanges = hasChanges(preview);

  return (
    <div className="col-12">
      <div className="admin-card" style={card}>
        <div className="admin-card__title" style={cardTitle}>{t("admin.masterData.title")}</div>
        <p style={{ marginTop: 4 }}>{t("admin.masterData.intro")}</p>

        <div style={{ display: "flex", gap: 12, flexWrap: "wrap", alignItems: "center", marginTop: 12 }}>
          <button type="button" style={btn(false, !!busy)} disabled={!!busy} onClick={download}>
            {busy === "download" ? t("admin.masterData.downloading") : t("admin.masterData.download")}
          </button>
          <label style={{ display: "flex", gap: 8, alignItems: "center", flexWrap: "wrap" }}>
            <span>{t("admin.masterData.chooseFile")}</span>
            <input ref={fileInputRef} type="file" accept=".xlsx" disabled={!!busy} onChange={pickFile} />
          </label>
          <button type="button" style={btn(false, !file || !!busy)} disabled={!file || !!busy} onClick={runPreview}>
            {busy === "preview" ? t("admin.masterData.previewing") : t("admin.masterData.previewButton")}
          </button>
        </div>

        {file && (
          <p style={{ margin: "8px 0 0", fontSize: 13 }}>
            {t("admin.masterData.selectedFile", { name: file.name, size: formatSize(file.size) })}
          </p>
        )}

        {error && <p style={{ color: "#b91c1c", marginTop: 12 }}>{error}</p>}

        {preview && (
          <div style={previewBox} role="region" aria-label={t("admin.masterData.preview.title")}>
            <strong style={{ fontSize: 15 }}>{t("admin.masterData.preview.title")}</strong>
            {previewHasChanges ? (
              <>
                <p style={{ margin: "6px 0 0" }}>{t("admin.masterData.preview.hint")}</p>
                <ResultSummary t={t} result={preview} />
                <p style={{ margin: "12px 0 0" }}>{t("admin.masterData.preview.backup")}</p>
                <div style={{ display: "flex", gap: 12, flexWrap: "wrap", marginTop: 8 }}>
                  <button type="button" style={btn(false, !!busy)} disabled={!!busy} onClick={download}>
                    {busy === "download" ? t("admin.masterData.downloading") : t("admin.masterData.backupButton")}
                  </button>
                  <button type="button" style={btn(true, !!busy, true)} disabled={!!busy} onClick={apply}>
                    {busy === "apply" ? t("admin.masterData.applying") : t("admin.masterData.applyButton")}
                  </button>
                </div>
              </>
            ) : (
              <p style={{ margin: "6px 0 0" }}>{t("admin.masterData.preview.nothing")}</p>
            )}
          </div>
        )}

        {applied && (
          <div style={{ marginTop: 16 }}>
            <strong style={{ color: "#15803d" }}>{t("admin.masterData.result.title")}</strong>
            {hasChanges(applied) ? (
              <ResultSummary t={t} result={applied} />
            ) : (
              <p style={{ marginTop: 8 }}>{t("admin.masterData.result.nothing")}</p>
            )}
          </div>
        )}
      </div>
    </div>
  );
}
